// SearchForm.js - Premium Design
import React, { useState } from 'react';
import AirportAutocomplete from '../common/AirportAutocomplete';
import LocationAutocomplete from '../common/LocationAutocomplete';
import { SERVICE_TYPES, BOOKING_TYPES, CITY_GEOFENCE } from '../../utils/constants';

const SearchForm = ({ onSearch, serviceType = SERVICE_TYPES.AIRPORT, premium = true }) => {
  const [subService, setSubService] = useState('transfer');
  
  const [formData, setFormData] = useState({
    fromPlace: '',           
    fromPlaceFull: '',       
    fromLat: null,
    fromLng: null,
    toPlace: '',             
    toPlaceFull: '',         
    toLat: null,
    toLng: null,
    pickupDate: new Date().toISOString().split('T')[0],
    pickupTime: '13:45',
    returnDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    returnTime: '13:45',
    passengers: 2,
    hours: 2,
  });
  const [showReturn, setShowReturn] = useState(false);
  const [geofenceError, setGeofenceError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isWithinGeofence = (lat, lng) => {
    return (
      lat >= CITY_GEOFENCE.bounds.south &&
      lat <= CITY_GEOFENCE.bounds.north &&
      lng >= CITY_GEOFENCE.bounds.west &&
      lng <= CITY_GEOFENCE.bounds.east
    );
  };

  const handleFromPlaceSelect = (placeData) => {
    if (serviceType === SERVICE_TYPES.CITY && subService === 'transfer') {
      if (!isWithinGeofence(placeData.lat, placeData.lng)) {
        setGeofenceError('Pickup location must be within Colombo or Negombo area');
      } else {
        setGeofenceError('');
      }
    } else {
      setGeofenceError('');
    }

    setFormData(prev => ({
      ...prev,
      fromPlace: placeData.displayName || placeData.address,
      fromPlaceFull: placeData.address,
      fromLat: placeData.lat,
      fromLng: placeData.lng
    }));
  };

  const handleToPlaceSelect = (placeData) => {
    setFormData(prev => ({
      ...prev,
      toPlace: placeData.displayName || placeData.address,
      toPlaceFull: placeData.address,
      toLat: placeData.lat,
      toLng: placeData.lng
    }));
  };

  const handleSubServiceChange = (service) => {
    setSubService(service);
    setGeofenceError('');
    
    if (serviceType === SERVICE_TYPES.CITY && service === 'transfer' && formData.fromLat) {
      if (!isWithinGeofence(formData.fromLat, formData.fromLng)) {
        setGeofenceError('Pickup location must be within Colombo or Negombo area');
      }
    }
  };

  const getBookingType = () => {
    if (serviceType === SERVICE_TYPES.AIRPORT) {
      return subService === 'transfer' ? BOOKING_TYPES.AIRPORT_TRANSFER : BOOKING_TYPES.AIRPORT_HOURLY;
    } else {
      return subService === 'transfer' ? BOOKING_TYPES.CITY_POINT_TO_POINT : BOOKING_TYPES.CITY_HOURLY;
    }
  };

  const handleSubmit = () => {
    if (!formData.fromPlace) {
      alert('Please select a pickup location');
      return;
    }

    if (serviceType === SERVICE_TYPES.CITY && subService === 'transfer') {
      if (!isWithinGeofence(formData.fromLat, formData.fromLng)) {
        alert('Pickup location must be within Colombo or Negombo area for City Transport');
        return;
      }
    }

    if (subService === 'transfer' && !formData.toPlace) {
      alert('Please select a destination');
      return;
    }

    if (!formData.fromLat || !formData.fromLng) {
      alert('Please select a valid pickup location from the suggestions');
      return;
    }

    if (subService === 'transfer' && (!formData.toLat || !formData.toLng)) {
      alert('Please select a valid destination from the suggestions');
      return;
    }

    const formatLocationDetails = (shortName, fullAddress) => {
      if (!fullAddress) return shortName || '';
      if (!shortName || shortName === fullAddress) return fullAddress;
      if (fullAddress.toLowerCase().startsWith(shortName.toLowerCase())) return fullAddress;
      return `${shortName}, ${fullAddress}`;
    };

    const bookingType = getBookingType();
    const searchData = {
      type: bookingType,
      serviceType,
      subService,
      fromPlace: formatLocationDetails(formData.fromPlace, formData.fromPlaceFull),
      fromLat: formData.fromLat,
      fromLng: formData.fromLng,
      toPlace: formatLocationDetails(formData.toPlace, formData.toPlaceFull),
      toLat: formData.toLat,
      toLng: formData.toLng,
      pickupDate: formData.pickupDate,
      pickupTime: formData.pickupTime,
      returnDate: formData.returnDate,
      returnTime: formData.returnTime,
      passengers: formData.passengers,
      hours: formData.hours,
      showReturn,
      pickupAt: new Date(`${formData.pickupDate}T${formData.pickupTime}:00`),
    };
    
    onSearch(searchData);
  };

  const containerClasses = premium 
    ? "w-full" 
    : "bg-white rounded-xl shadow-2xl p-5 w-full border border-gray-100";

  const labelClasses = premium 
    ? "block text-xs font-medium text-gray-400 mb-1" 
    : "block text-xs font-medium text-gray-600 mb-1";

  const inputClasses = premium 
    ? "w-full px-3 py-3 border border-gray-700 rounded-lg bg-gray-900 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm" 
    : "w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-black transition-all text-sm";

  const buttonPrimaryClasses = premium
    ? "w-full bg-blue-600 text-white py-4 rounded-lg font-bold text-sm hover:bg-blue-700 transition-all active:scale-[0.98] uppercase tracking-wide shadow-lg shadow-blue-500/20"
    : "w-full bg-black text-white py-3 rounded-lg font-bold text-sm hover:bg-gray-800 transition-all active:scale-[0.98] uppercase tracking-wide";

  const tabClasses = (active) => premium
    ? `flex-1 py-2 px-3 rounded-lg border transition-all text-sm font-medium ${
        active 
          ? 'border-blue-500 bg-blue-500/20 text-white' 
          : 'border-gray-700 text-gray-400 hover:border-gray-600 bg-gray-800/50'
      }`
    : `flex-1 py-2 px-3 rounded-lg border-2 transition-all text-sm font-medium ${
        active 
          ? 'border-black bg-black text-white' 
          : 'border-gray-200 text-gray-600 hover:border-gray-300 bg-gray-50'
      }`;

  return (
    <div className={containerClasses}>
      {/* Sub-Service Tabs */}
      <div className="flex mb-4 gap-2">
        <button
          className={tabClasses(subService === 'transfer')}
          onClick={() => handleSubServiceChange('transfer')}
        >
          {serviceType === SERVICE_TYPES.AIRPORT ? 'Airport Transfer' : 'Point-to-Point'}
        </button>
        <button
          className={tabClasses(subService === 'hourly')}
          onClick={() => handleSubServiceChange('hourly')}
        >
          Hourly Service
        </button>
      </div>

      {/* Location Inputs */}
      <div className="space-y-4 mb-4">
        {serviceType === SERVICE_TYPES.AIRPORT && subService === 'transfer' ? (
          <AirportAutocomplete
            label="From (Airport)"
            value={formData.fromPlace}
            onChange={(value) => handleInputChange('fromPlace', value)}
            onSelect={handleFromPlaceSelect}
            placeholder="Select airport..."
            compact={true}
            dark={premium}
          />
        ) : (
          <div>
            <LocationAutocomplete
              label={
                serviceType === SERVICE_TYPES.CITY && subService === 'transfer' 
                  ? "From (Colombo/Negombo area)" 
                  : "From"
              }
              value={formData.fromPlace}
              onChange={(value) => handleInputChange('fromPlace', value)}
              onSelect={handleFromPlaceSelect}
              placeholder="Address, hotel, city..."
              compact={true}
              dark={premium}
            />
            {geofenceError && (
              <p className="text-red-500 text-xs mt-1 flex items-center">
                <span className="mr-1">⚠️</span>
                {geofenceError}
              </p>
            )}
          </div>
        )}
        
        {subService === 'transfer' && (
          <LocationAutocomplete
            label={serviceType === SERVICE_TYPES.AIRPORT ? "To" : "To (Anywhere in Sri Lanka)"}
            value={formData.toPlace}
            onChange={(value) => handleInputChange('toPlace', value)}
            onSelect={handleToPlaceSelect}
            placeholder="Address, hotel, city..."
            compact={true}
            dark={premium}
          />
        )}
      </div>

      {/* Date and Time */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className={labelClasses}>Pickup date</label>
          <input
            type="date"
            className={inputClasses}
            value={formData.pickupDate}
            onChange={(e) => handleInputChange('pickupDate', e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            style={premium ? { colorScheme: 'dark' } : {}}
          />
        </div>
        <div>
          <label className={labelClasses}>Pickup time</label>
          <input
            type="time"
            className={inputClasses}
            value={formData.pickupTime}
            onChange={(e) => handleInputChange('pickupTime', e.target.value)}
            style={premium ? { colorScheme: 'dark' } : {}}
          />
        </div>
      </div>

      {/* Duration - Hourly Only */}
      {subService === 'hourly' && (
        <div className="mb-4">
          <label className={labelClasses}>Duration</label>
          <select 
            className={inputClasses}
            value={formData.hours}
            onChange={(e) => handleInputChange('hours', parseInt(e.target.value))}
            style={premium ? { colorScheme: 'dark' } : {}}
          >
            {[2,3,4,5,6,8,10,12].map(hour => (
              <option key={hour} value={hour}>{hour} Hours</option>
            ))}
          </select>
          <p className={`text-xs mt-1 ${premium ? 'text-gray-500' : 'text-gray-500'}`}>
            {formData.hours * 30} km included
          </p>
        </div>
      )}

      {/* Return Trip Toggle */}
      {subService === 'transfer' && (
        <div className="mb-4 text-center">
          <button
            type="button"
            className={`${premium ? 'text-blue-400' : 'text-gray-700'} text-xs font-semibold hover:underline transition-all uppercase tracking-wide`}
            onClick={() => setShowReturn(!showReturn)}
          >
            {showReturn ? '− Remove Return Trip' : '+ Add Return Trip'}
          </button>
        </div>
      )}

      {/* Return Date/Time */}
      {showReturn && subService === 'transfer' && (
        <div className={`grid grid-cols-2 gap-3 mb-4 p-3 ${premium ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-lg border ${premium ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <label className={labelClasses}>Return date</label>
            <input
              type="date"
              className={inputClasses}
              value={formData.returnDate}
              onChange={(e) => handleInputChange('returnDate', e.target.value)}
              min={formData.pickupDate}
              style={premium ? { colorScheme: 'dark' } : {}}
            />
          </div>
          <div>
            <label className={labelClasses}>Return time</label>
            <input
              type="time"
              className={inputClasses}
              value={formData.returnTime}
              onChange={(e) => handleInputChange('returnTime', e.target.value)}
              style={premium ? { colorScheme: 'dark' } : {}}
            />
          </div>
        </div>
      )}

      {/* Passengers */}
      <div className="mb-6">
        <label className={labelClasses}>Passengers</label>
        <div className="flex items-center justify-between bg-gray-800/30 rounded-lg p-1 border border-gray-700">
          <button
            type="button"
            className="w-12 h-12 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-700 hover:text-white transition-all active:scale-95 text-xl font-bold"
            onClick={() => handleInputChange('passengers', Math.max(1, formData.passengers - 1))}
          >
            −
          </button>
          <span className={`text-xl font-semibold ${premium ? 'text-white' : 'text-gray-800'}`}>{formData.passengers}</span>
          <button
            type="button"
            className="w-12 h-12 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-700 hover:text-white transition-all active:scale-95 text-xl font-bold"
            onClick={() => handleInputChange('passengers', formData.passengers + 1)}
          >
            +
          </button>
        </div>
      </div>

      {/* Search Button */}
      <button
        onClick={handleSubmit}
        className={buttonPrimaryClasses}
      >
        Search Available Vehicles
      </button>
    </div>
  );
};

export default SearchForm;